import { useMemo } from 'react';

import { SpendChartCard } from '@apps/web/components/admin/llm_spend/chart_shared';
import {
    generationSeriesFor,
    OBJECTIVE_SERIES,
} from '@apps/web/components/admin/llm_spend/constants';
import { GenerationSelect } from '@apps/web/components/admin/llm_spend/generation_select';
import { ObjectiveBreakdown } from '@apps/web/components/admin/llm_spend/objective_breakdown';
import { SpendLineChart } from '@apps/web/components/admin/llm_spend/spend_line_chart';
import {
    formatCount,
    formatUsd,
    generationRatioPoints,
    ratio,
} from '@apps/web/components/admin/llm_spend/spend_math';
import {
    OutliersTable,
    OutreachResponseOutcomesTable,
    OutreachStatusesTable,
} from '@apps/web/components/admin/llm_spend/spend_tables';
import { type GenerationTabProps } from '@apps/web/components/admin/llm_spend/tab_types';
import { KpiStatCard } from '@apps/web/components/admin/outreach_observability/kpi_stat_card';

export function OutreachTab({
    data,
    bucket,
    generation,
    onGenerationChange,
}: GenerationTabProps) {
    const generationBuckets = useMemo(
        () =>
            data.buckets.map((row) => ({
                bucket_start: row.bucket_start,
                by_generation: row.outreach_by_generation,
            })),
        [data.buckets],
    );
    const costPerOutreachPoints = generationRatioPoints(
        generationBuckets,
        (metrics) => [
            metrics.tracked_estimated_spend_usd,
            metrics.outreach_count,
        ],
    );
    const callsPerOutreachPoints = generationRatioPoints(
        generationBuckets,
        (metrics) => [metrics.llm_call_count, metrics.outreach_count],
    );
    const wakesPerOutreachPoints = generationRatioPoints(
        generationBuckets,
        (metrics) => [metrics.wake_count, metrics.outreach_count],
    );
    const generationSeries = generationSeriesFor(generation);
    return (
        <div className="flex flex-col gap-4">
            <GenerationSelect
                value={generation}
                onValueChange={onGenerationChange}
            />
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <KpiStatCard
                    label="Outreach spend"
                    value={formatUsd(data.totals.outreach_spend_usd)}
                    compact={true}
                />
                <KpiStatCard
                    label="Outreaches created"
                    value={data.totals.outreach_count.toLocaleString()}
                    compact={true}
                />
                <KpiStatCard
                    label="Spend per outreach"
                    value={formatUsd(
                        ratio(
                            data.totals.outreach_spend_usd,
                            data.totals.outreach_count,
                        ),
                    )}
                    compact={true}
                />
                <KpiStatCard
                    label="Activity per outreach"
                    value={`${formatCount(
                        ratio(
                            data.totals.outreach_llm_call_count,
                            data.totals.outreach_count,
                        ),
                    )} LLM calls · ${formatCount(
                        ratio(
                            data.totals.outreach_wake_count,
                            data.totals.outreach_count,
                        ),
                    )} wakes`}
                    valueSize="sm"
                    compact={true}
                />
            </div>
            <div className="grid gap-4 xl:grid-cols-3">
                <SpendChartCard title="Cost per outreach">
                    <SpendLineChart
                        points={costPerOutreachPoints}
                        series={generationSeries}
                        bucket={bucket}
                        valueFormatter={formatUsd}
                    />
                </SpendChartCard>
                <SpendChartCard title="LLM calls per outreach">
                    <SpendLineChart
                        points={callsPerOutreachPoints}
                        series={generationSeries}
                        bucket={bucket}
                        valueFormatter={(value) => formatCount(value)}
                    />
                </SpendChartCard>
                <SpendChartCard title="Wakes per outreach">
                    <SpendLineChart
                        points={wakesPerOutreachPoints}
                        series={generationSeries}
                        bucket={bucket}
                        valueFormatter={(value) => formatCount(value)}
                    />
                </SpendChartCard>
            </div>
            <ObjectiveBreakdown
                title="Outreach cost by objective"
                bucket={bucket}
                buckets={data.buckets.map((row) => ({
                    bucketStart: row.bucket_start,
                    spend: row.spend_by_objective,
                    wakes: row.wakes_by_objective,
                    units: row.outreach_count,
                }))}
                series={OBJECTIVE_SERIES}
                totalUnits={data.totals.outreach_count}
                unitLabel="outreach"
            />
            <OutreachStatusesTable rows={data.outreach_statuses} />
            <OutreachResponseOutcomesTable
                rows={data.outreach_response_outcomes}
            />
            <OutliersTable rows={data.outliers} />
        </div>
    );
}
